/**
 * Arrastre de la grilla de widgets (dashboard y ficha de contacto):
 *  - Al tomar un widget, la grilla entera se achica hasta entrar en la pantalla, anclada
 *    en el puntero, para llevarlo a cualquier lado sin scrollear.
 *  - Al soltarlo junto a otro donde no entra entero, toma el ancho del hueco.
 *
 * El componente tiene que tener `$refs.grid` (grid-layout), `$refs.canvas` (el div que
 * la envuelve, con `:style="canvasStyle"` y `@pointerdown.capture="maybeZoomOut"`),
 * `cols`, `margin`, `visibleWidgets`, `entryOf(w)` y `scheduleSave()`.
 */
export default {
  data() {
    return {
      dragScale: 1,
      // Cerca del borde de la ventana, la página se desplaza sola mientras se arrastra.
      dragOption: { autoScroll: { enabled: true, margin: 80, speed: 1100 } },
      dragOrigin: "0px 0px",
    };
  },
  computed: {
    canvasStyle() {
      if (this.dragScale >= 1) return null;
      return {
        transform: `scale(${this.dragScale})`,
        transformOrigin: this.dragOrigin,
      };
    },
  },
  updated() {
    this.installDragFit();
  },
  beforeDestroy() {
    this.stopPointerTracking();
    document.removeEventListener("pointerup", this.endZoom, true);
    document.removeEventListener("pointercancel", this.endZoom, true);
  },
  methods: {
    /**
     * Al arrastrar un widget junto a otro donde no entra entero, toma el ancho del
     * hueco (sin bajar de su mínimo); si después se lo lleva a un lugar con espacio,
     * vuelve a su ancho. vue-grid-layout no lo hace: empuja al vecino para abajo.
     *
     * Se envuelve `dragEvent` de la grilla (el bus de eventos lo llama por nombre en
     * cada movimiento) para corregir x/w antes de que ubique y compacte.
     */
    installDragFit() {
      const grid = this.$refs.grid;
      if (!grid || grid.__dwDragFit) return;
      const original = grid.dragEvent;
      grid.__dwDragFit = true;
      grid.dragEvent = (eventName, id, x, y, h, w) => {
        const fit = this.fitDraggedWidth(eventName, id, x, y);
        if (fit) {
          x = fit.x;
          w = fit.w;
        }
        const out = original.call(grid, eventName, id, x, y, h, w);
        if (eventName === "dragend") {
          this.stopPointerTracking();
          if (fit && fit.changed) this.scheduleSave();
          this.dragStartW = {};
          this.dragStartLayout = null;
        }
        return out;
      };
    },
    /**
     * Escala para que toda la grilla entre en la pantalla sin mover el punto que está
     * bajo el puntero (transform-origin ahí): el widget tomado sigue bajo el mouse.
     * Se aplica al apretar, antes de que arranque el arrastre, porque la grilla mide
     * las posiciones al empezar.
     */
    maybeZoomOut(e) {
      if (e.button !== undefined && e.button !== 0) return;
      const target = e.target;
      if (!target || !target.closest || !target.closest(".dw-head")) return;
      if (target.closest("button, a, input, textarea, select, .dw-actions"))
        return;
      const canvas = this.$refs.canvas;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const height = canvas.offsetHeight;
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;
      const pad = 12;
      const above = offsetY > 0 ? (e.clientY - pad) / offsetY : 1;
      const below =
        height - offsetY > 0
          ? (window.innerHeight - pad - e.clientY) / (height - offsetY)
          : 1;
      // Con un mínimo legible: si la grilla es muy alta, lo que falte lo hace el
      // auto-scroll al acercarse al borde.
      const scale = Math.max(0.4, Math.min(1, above, below));
      if (scale > 0.95) return;
      this.dragOrigin = `${offsetX}px ${offsetY}px`;
      this.dragScale = Math.round(scale * 1000) / 1000;
      document.addEventListener("pointerup", this.endZoom, true);
      document.addEventListener("pointercancel", this.endZoom, true);
    },
    endZoom() {
      document.removeEventListener("pointerup", this.endZoom, true);
      document.removeEventListener("pointercancel", this.endZoom, true);
      // Después del dragend de la grilla, que todavía calcula con la escala.
      setTimeout(() => {
        this.dragScale = 1;
      }, 0);
    },
    startPointerTracking() {
      if (this.onPointerMove) return;
      this.onPointerMove = (e) => {
        const p = e.touches ? e.touches[0] : e;
        if (p) this.pointerX = p.clientX;
      };
      document.addEventListener("pointermove", this.onPointerMove, true);
      document.addEventListener("mousemove", this.onPointerMove, true);
      document.addEventListener("touchmove", this.onPointerMove, {
        capture: true,
        passive: true,
      });
    },
    stopPointerTracking() {
      if (!this.onPointerMove) return;
      document.removeEventListener("pointermove", this.onPointerMove, true);
      document.removeEventListener("mousemove", this.onPointerMove, true);
      document.removeEventListener("touchmove", this.onPointerMove, true);
      this.onPointerMove = null;
    },
    /** Columna de la grilla bajo el puntero, o null si no se sabe. */
    pointerColumn() {
      const grid = this.$refs.grid;
      if (!grid || !grid.$el || typeof this.pointerX !== "number") return null;
      // El rect viene escalado si la grilla está achicada; las columnas, no.
      const rect = grid.$el.getBoundingClientRect();
      const scale = this.dragScale || 1;
      const colWidth =
        (grid.$el.offsetWidth - this.margin * (this.cols + 1)) / this.cols;
      const localX = (this.pointerX - rect.left) / scale;
      const col = Math.floor(
        (localX - this.margin / 2) / (colWidth + this.margin)
      );
      return Math.min(Math.max(col, 0), this.cols - 1);
    },
    fitDraggedWidth(eventName, id, x, y) {
      const item = this.visibleWidgets.find((w) => w.i === id);
      if (!item) return null;
      if (!this.dragStartW) this.dragStartW = {};
      if (eventName === "dragstart") {
        this.dragStartW[id] = item.w;
        // Los huecos se miden contra cómo estaba todo al empezar: mientras se arrastra,
        // la grilla va empujando vecinos y esas posiciones de paso no sirven.
        this.dragStartLayout = this.visibleWidgets.map(
          ({ i, x: ox, y: oy, w: ow, h: oh }) => ({
            i,
            x: ox,
            y: oy,
            w: ow,
            h: oh,
          })
        );
        this.startPointerTracking();
        return null;
      }
      const fullW = this.dragStartW[id] || item.w;
      const minW = Math.min(this.entryOf(item).min.w, fullW);
      const clampX = (cx, width) =>
        Math.min(Math.max(cx, 0), this.cols - width);

      let next = { x: clampX(x, fullW), w: fullW };
      const col = this.pointerColumn();
      if (col !== null) {
        // Columnas ocupadas por los widgets que comparten filas con el arrastrado.
        const busy = new Array(this.cols).fill(false);
        (this.dragStartLayout || this.visibleWidgets).forEach((o) => {
          if (o.i === id || o.y >= y + item.h || o.y + o.h <= y) return;
          for (let c = o.x; c < Math.min(o.x + o.w, this.cols); c += 1)
            busy[c] = true;
        });
        if (!busy[col]) {
          let start = col;
          let end = col + 1;
          while (start > 0 && !busy[start - 1]) start -= 1;
          while (end < this.cols && !busy[end]) end += 1;
          const free = end - start;
          if (free >= fullW)
            next = { x: Math.min(Math.max(x, start), end - fullW), w: fullW };
          else if (free >= minW) next = { x: start, w: free };
        }
      }
      item.w = next.w;
      return { ...next, changed: next.w !== this.dragStartW[id] };
    },
  },
};
